import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  Grid,
  Chip,
  Button,
  IconButton,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Refresh as RefreshIcon,
  Visibility as EyeIcon,
  Palette as ColorIcon,
  Face as FaceIcon,
  Label as LabelIcon,
  TextFields as TextIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { getImageDetails, deleteImage, analyzeImage, getImageUrl, getThumbnailUrl } from '../services/api';
import { downloadImage } from '../utils/downloadUtils';
import LabelObjectEditor from './LabelObjectEditor';

const MotionCard = motion(Card);

const ImageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    loadImageDetails();
  }, [id]);

  const loadImageDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const details = await getImageDetails(id);
      setImage(details);
    } catch (err) {
      console.error('Error loading image details:', err);
      setError('Failed to load image details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteImage(id);
      navigate('/');
    } catch (err) {
      console.error('Error deleting image:', err);
      setError('Failed to delete image');
    }
    setDeleteDialogOpen(false);
  };

  const handleReanalyze = async () => {
    setAnalyzing(true);

    try {
      const updatedMetadata = await analyzeImage(id);
      setImage(prev => ({ ...prev, ...updatedMetadata }));
    } catch (err) {
      console.error('Error reanalyzing image:', err);
      setError('Failed to reanalyze image');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDownload = async () => {
    if (image) {
      await downloadImage(image, null, () => {
        setError('Failed to download image. Please try again.');
      });
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'AI Photo Gallery - Image',
          url: window.location.href,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      // You could show a toast notification here
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (error || !image) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          {error || 'Image not found'}
        </Alert>
      </Box>
    );
  }

  const formatFileSize = (bytes) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
          Image Details
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          startIcon={analyzing ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={handleReanalyze}
          disabled={analyzing}
          sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
        >
          {analyzing ? 'Analyzing...' : 'Re-analyze'}
        </Button>
        <IconButton onClick={handleShare} sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          <ShareIcon />
        </IconButton>
        <IconButton onClick={handleDownload} sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          <DownloadIcon />
        </IconButton>
        <IconButton
          onClick={() => setDeleteDialogOpen(true)}
          sx={{ color: 'rgba(244, 67, 54, 0.8)' }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>

      <Grid container spacing={4}>
        {/* Image Display */}
        <Grid item xs={12} md={8}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            sx={{
              background: 'rgba(26, 26, 46, 0.8)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <CardMedia
              component="img"
              image={getImageUrl(image.file_path, image.s3_url, image.id)}
              alt={image.description || 'Image'}
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
            />
          </MotionCard>
        </Grid>

        {/* Metadata Panel */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              background: 'rgba(26, 26, 46, 0.8)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            {/* Basic Info */}
            <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
              Image Information
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
                Uploaded: {formatDate(image.upload_date)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
                Size: {formatFileSize(image.file_size)}
              </Typography>
              {image.dimensions ? (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
                  Dimensions: {image.dimensions[0]} × {image.dimensions[1]}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
                  Format: AI-analyzed image
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                ID: {image.id}
              </Typography>
            </Box>

            <Divider sx={{ my: 2, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />

            {/* Description */}
            {image.description && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  Description
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  {image.description}
                </Typography>
              </Box>
            )}

            {/* Editable Labels and Objects */}
            <LabelObjectEditor
              imageId={id}
              onUpdate={loadImageDetails}
            />

            {/* Text Content */}
            {image.google_vision_text && image.google_vision_text.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TextIcon sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Text in Image
                  </Typography>
                </Box>
                <List dense>
                  {image.google_vision_text.slice(0, 5).map((text, idx) => {
                    // Handle both old format (strings) and new AI format (objects)
                    const textContent = typeof text === 'string' ? text : text?.text || text;

                    return (
                      <ListItem key={idx} sx={{ px: 0 }}>
                        <ListItemText
                          primary={textContent}
                          sx={{
                            '& .MuiListItemText-primary': {
                              color: 'rgba(255, 255, 255, 0.9)',
                              fontSize: '0.875rem',
                            }
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}

            {/* Faces */}
            {image.google_vision_faces > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FaceIcon sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Faces Detected
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  {image.google_vision_faces} face{image.google_vision_faces > 1 ? 's' : ''} detected
                </Typography>
              </Box>
            )}

            {/* Colors */}
            {image.dominant_colors && image.dominant_colors.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ColorIcon sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Dominant Colors
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {image.dominant_colors.map((color, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '4px',
                        backgroundColor: color.rgb,
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                      title={`${color.rgb} (${Math.round(color.score * 100)}%)`}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            background: 'rgba(26, 26, 46, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>
          Delete Image
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Are you sure you want to delete this image? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} sx={{ color: '#f44336' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageDetail;
